/**
 * Theme Selector Component
 * 
 * A dropdown component for switching between available themes
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTheme, useThemeClasses } from '../providers/ThemeProvider';
import { cx } from '../styles';

interface ThemeSelectorProps {
  className?: string;
  variant?: 'dropdown' | 'buttons' | 'compact';
  showIcons?: boolean;
}

export const ThemeSelector: React.FC<ThemeSelectorProps> = ({ 
  className = '',
  variant = 'dropdown',
  showIcons = true 
}) => {
  const { currentTheme, themePreference, setThemePreference, availableThemes } = useTheme();
  const themeClasses = useThemeClasses();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getThemeIcon = (themePreference: string, isDark: boolean) => {
    if (!showIcons) return null;
    
    switch (themePreference) {
      case 'light':
        return '☀️';
      case 'dark':
        return '🌙';
      case 'auto':
        return '🔄';
      case 'high-contrast':
        return '🔳';
      default:
        return isDark ? '🌙' : '☀️';
    }
  };

  const getCurrentDisplayName = () => {
    const currentPref = availableThemes.find(theme => theme.name === themePreference);
    return currentPref?.displayName || 'Unknown';
  };

  // Compact variant - just an icon button
  if (variant === 'compact') {
    return (
      <div className={cx('relative', className)} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={cx(
            'flex items-center justify-center w-10 h-10 rounded-lg transition-colors',
            'bg-white/10 hover:bg-white/20 text-white',
            'focus:outline-none focus:ring-2 focus:ring-white/30'
          )}
          aria-label="Switch theme"
        >
          <span className="text-lg">
            {getThemeIcon(themePreference, currentTheme.isDark)}
          </span>
        </button>

        {isOpen && (
          <div className={cx(
            'absolute right-0 mt-2 w-48 rounded-lg shadow-lg border z-50 py-1',
            themeClasses.bgSurface,
            themeClasses.borderPrimary
          )}>
            {availableThemes.map((theme) => (
              <button
                key={theme.name}
                onClick={() => {
                  setThemePreference(theme.name);
                  setIsOpen(false);
                }}
                className={cx(
                  'w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-3',
                  themeClasses.hover,
                  themePreference === theme.name ? themeClasses.bgInfoSubtle + ' ' + themeClasses.textInfo : ''
                )}
              >
                <span className="text-base">
                  {getThemeIcon(theme.name, theme.isDark ?? currentTheme.isDark)}
                </span>
                <span>{theme.displayName}</span>
                {themePreference === theme.name && (
                  <span className={cx("ml-auto", themeClasses.textInfo)}>✓</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Button variant - toggle buttons
  if (variant === 'buttons') {
    return (
      <div className={cx('flex gap-2', className)}>
        {availableThemes.map((theme) => (
          <button
            key={theme.name}
            onClick={() => setThemePreference(theme.name)}
            className={cx(
              'flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all',
              themePreference === theme.name
                ? 'bg-blue-600 text-white shadow-md'
                : cx(themeClasses.bgSecondary, themeClasses.textSecondary, themeClasses.hover)
            )}
          >
            {showIcons && (
              <span>{getThemeIcon(theme.name, theme.isDark ?? currentTheme.isDark)}</span>
            )}
            <span>{theme.displayName}</span>
          </button>
        ))}
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={cx('relative', className)} ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cx(
          'flex items-center gap-3 px-4 py-2 rounded-lg border',
          'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
          'text-sm font-medium min-w-[140px]',
          themeClasses.bgSurface,
          themeClasses.textPrimary,
          themeClasses.borderPrimary,
          themeClasses.hover
        )}
      >
        {showIcons && (
          <span className="text-base">
            {getThemeIcon(themePreference, currentTheme.isDark)}
          </span>
        )}
        <span className="flex-1 text-left">{getCurrentDisplayName()}</span>
        <svg
          className={cx(
            'w-4 h-4 transition-transform',
            isOpen && 'rotate-180'
          )}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className={cx(
          'absolute right-0 mt-2 w-full rounded-lg shadow-lg border z-50 py-1',
          themeClasses.bgSurface,
          themeClasses.borderPrimary
        )}>
          {availableThemes.map((theme) => (
            <button
              key={theme.name}
              onClick={() => {
                setThemePreference(theme.name);
                setIsOpen(false);
              }}
              className={cx(
                'w-full text-left px-4 py-2 text-sm transition-colors flex items-center gap-3',
                themeClasses.hover,
                themePreference === theme.name ? themeClasses.bgInfoSubtle + ' ' + themeClasses.textInfo : ''
              )}
            >
              {showIcons && (
                <span className="text-base">
                  {getThemeIcon(theme.name, theme.isDark ?? currentTheme.isDark)}
                </span>
              )}
              <span className="flex-1">{theme.displayName}</span>
              {themePreference === theme.name && (
                <span className="text-blue-600">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ThemeSelector;
